import axios from "axios";

export enum CampaignCriteriaKind {
  PRESET = "preset",
  EPHEMERAL = "ephemeral",
}

export interface CampaignCriteria {
  id: number;
  "workspace-id": number;
  name: string;
  offer: string;
  "target-customer": string | null;
  "customer-action": string;
  "icp-but-bad-fit": string | null;
  kind: CampaignCriteriaKind;
  // The calibration budget is counted against this, so a rewrite earns
  // the criteria a fresh set of questions.
  "judgement-revision": number;
  "reviewed?": boolean;
  "archived?": boolean;
  "created-at": string;
  "updated-at": string;
}

// Null clears an optional field; omitting it leaves it alone.
export interface CampaignCriteriaRequest {
  name?: string;
  offer?: string;
  "target-customer"?: string | null;
  "customer-action"?: string;
  "icp-but-bad-fit"?: string | null;
  kind?: CampaignCriteriaKind;
  "reviewed?"?: boolean;
  "archived?"?: boolean;
}

const makeEndpoint = (workspaceSlug: string): string =>
  `/api/v1/workspaces/${workspaceSlug}/campaign-criteria`;

export const getCampaignCriteriaList = async (
  workspaceSlug: string,
  { kind, archived }: { kind?: CampaignCriteriaKind; archived?: boolean } = {},
): Promise<CampaignCriteria[]> => {
  const params = new URLSearchParams();
  if (kind !== undefined) {
    params.set("kind", kind);
  }
  if (archived) {
    params.set("archived", "true");
  }
  return (await axios.get(makeEndpoint(workspaceSlug), { params })).data;
};

export const getCampaignCriteria = async (
  workspaceSlug: string,
  id: number,
): Promise<CampaignCriteria> => {
  return (await axios.get(`${makeEndpoint(workspaceSlug)}/${id}`)).data;
};

export const createCampaignCriteria = async (
  workspaceSlug: string,
  data: CampaignCriteriaRequest,
): Promise<CampaignCriteria> => {
  return (await axios.post(makeEndpoint(workspaceSlug), data)).data;
};

export const patchCampaignCriteria = async (
  workspaceSlug: string,
  id: number,
  data: CampaignCriteriaRequest,
): Promise<CampaignCriteria> => {
  return (await axios.patch(`${makeEndpoint(workspaceSlug)}/${id}`, data)).data;
};

// Archives rather than deletes: the agents that ran against it keep the
// terms they rejected, and those terms are the only handle on the
// negative keywords in Google.
export const archiveCampaignCriteria = async (
  workspaceSlug: string,
  id: number,
): Promise<CampaignCriteria> => {
  return (await axios.delete(`${makeEndpoint(workspaceSlug)}/${id}`)).data;
};

// Answers with an ephemeral copy to edit. It is never reviewed, however
// vetted the original was — that vetting spoke for the original's wording.
export const forkCampaignCriteria = async (
  workspaceSlug: string,
  id: number,
  { name }: { name?: string } = {},
): Promise<CampaignCriteria> => {
  return (
    await axios.post(`${makeEndpoint(workspaceSlug)}/${id}/fork`, { name })
  ).data;
};
